import Cookies from 'js-cookie';
import { AUTH_API_BASE } from '@/config/apiConfig';
import {
  getAccessToken,
  getDeviceId,
  setAccessToken,
  setAuthCookies,
  clearAuthCookies,
  clearAuthSession,
} from '@/utils/authStorage';
import { emitSessionExpired } from '@/utils/sessionExpired';

export {
  getAccessToken,
  getDeviceId,
  setAuthCookies,
  clearAuthCookies,
  clearAuthSession,
} from '@/utils/authStorage';

// Event system for token refresh (publish-subscribe pattern)
class TokenRefreshEventSystem {
  private listeners: Array<(newToken: string) => void> = [];

  subscribe(callback: (newToken: string) => void) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(listener => listener !== callback);
    };
  }

  publish(newToken: string) {
    this.listeners.forEach(listener => listener(newToken));
  }
}

export const tokenRefreshEventSystem = new TokenRefreshEventSystem();

// Check if we're in a webview
const isWebView = (): boolean => {
  return sessionStorage.getItem('isNativeWebView') === 'true';
};

// Send message to React Native WebView
const sendMessageToNative = (message: any) => {
  if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
    window.ReactNativeWebView.postMessage(JSON.stringify(message));
  }
};

// Global message listener for token refresh (set up once)
let globalMessageListenerSetup = false;

const setupGlobalMessageListener = () => {
  if (globalMessageListenerSetup || !isWebView()) return;
  
  const handleGlobalMessage = (event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      
      if (data.type === 'TOKEN_REFRESHED') {
        console.log('[API Service] Received token refresh response globally', data);
        
        if (data.accessToken) {
          setAccessToken(data.accessToken);
          tokenRefreshEventSystem.publish(data.accessToken);
        }
      } else if (data.type === 'TOKEN_REFRESH_FAILED') {
        console.error('[API Service] Token refresh failed globally', data.error);
        tokenRefreshEventSystem.publish('ERROR');
        emitSessionExpired();
      }
    } catch (err) {
      console.warn('[API Service] Error parsing global message:', err);
    }
  };

  window.addEventListener('message', handleGlobalMessage);
  document.addEventListener('message', handleGlobalMessage);
  globalMessageListenerSetup = true;
  
  console.log('[API Service] Global message listener set up for token refresh');
};

// Request token refresh from React Native
const requestTokenRefresh = (): Promise<string> => {
  return new Promise((resolve, reject) => {
    // Set up global message listener if not already done
    setupGlobalMessageListener();
    
    const timeout = setTimeout(() => {
      reject(new Error('Token refresh timeout'));
    }, 15000); // 15 second timeout

    const unsubscribe = tokenRefreshEventSystem.subscribe((newToken) => {
      clearTimeout(timeout);
      unsubscribe();
      if (newToken === 'ERROR') {
        reject(new Error('Token refresh failed'));
      } else {
        resolve(newToken);
      }
    });

    // Send refresh request to React Native
    sendMessageToNative({
      type: 'REFRESH_TOKEN_REQUEST'
    });
  });
};

// Enhanced API fetch with 401 handling
type ApiFetchOptions = RequestInit & {
  skipAuth?: boolean;
};

export async function apiFetch(
  url: string,
  options: ApiFetchOptions = {}
): Promise<Response> {
  const accessToken = getAccessToken();
  const { skipAuth, ...requestOptions } = options;
  
  // Add authorization header if token exists
  const headers = {
    ...requestOptions.headers,
    ...(!skipAuth && accessToken && { 'Authorization': `Bearer ${accessToken}` })
  };

  const fetchOptions = {
    ...requestOptions,
    headers
  };

  let response = await fetch(url, fetchOptions);

  // Handle 401 Unauthorized — attempt refresh only when the user had a session
  if (response.status === 401 && !skipAuth) {
    const refreshToken = Cookies.get('refreshToken');
    const hadSession = Boolean(accessToken || refreshToken);

    if (!hadSession) {
      return response;
    }

    console.log('[API Service] Received 401, attempting token refresh...');
    console.log('[API Service] Is webview:', isWebView());

    if (isWebView()) {
      try {
        const newToken = await requestTokenRefresh();
        setAccessToken(newToken);

        const retryHeaders = {
          ...fetchOptions.headers,
          Authorization: `Bearer ${newToken}`,
        };

        response = await fetch(url, { ...fetchOptions, headers: retryHeaders });

        if (response.status === 401) {
          emitSessionExpired();
        }
      } catch (error) {
        console.error('[API Service] Token refresh failed:', error);
        emitSessionExpired();
      }
    } else {
      const deviceId = getDeviceId();

      if (!refreshToken || !deviceId) {
        emitSessionExpired();
        return response;
      }

      try {
        const refreshResponse = await fetch(`${AUTH_API_BASE}/api/auth/refresh-token`, {
          method: 'POST',
          headers: {
            accept: '*/*',
            accesstoken: accessToken || '',
            refreshtoken: refreshToken,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ deviceId }),
        });

        if (!refreshResponse.ok) {
          console.error('[API Service] Refresh token request failed:', refreshResponse.status);
          emitSessionExpired();
          return response;
        }

        const data = await refreshResponse.json();
        if (!data.accessToken || !data.refreshToken) {
          emitSessionExpired();
          return response;
        }

        setAuthCookies({
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
          deviceId,
        });

        const retryHeaders = {
          ...fetchOptions.headers,
          Authorization: `Bearer ${data.accessToken}`,
        };

        response = await fetch(url, { ...fetchOptions, headers: retryHeaders });

        if (response.status === 401) {
          emitSessionExpired();
        }
      } catch (refreshError) {
        console.error('[API Service] Web token refresh failed:', refreshError);
        emitSessionExpired();
      }
    }
  }

  return response;
}

// Export the token refresh event system for external use
export { TokenRefreshEventSystem };
